import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { useState, useEffect } from 'react';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

interface SubNavItem {
  name: string;
  href: string;
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ReactElement;
  subItems?: SubNavItem[];
}

const navigation: NavItem[] = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
        />
      </svg>
    ),
  },
  {
    name: 'Clients',
    href: '/clients',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
        />
      </svg>
    ),
  },
  {
    name: 'Projects',
    href: '/projects',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
        />
      </svg>
    ),
  },
  {
    name: 'Tasks',
    href: '/tasks',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
        />
      </svg>
    ),
  },
  {
    name: 'Finances',
    href: '/finances',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    subItems: [
      { name: 'Overview', href: '/finances' },
      { name: 'Invoices', href: '/finances/invoices' },
      { name: 'Expenses (Coming Soon)', href: '/finances/expenses' },
    ],
  },
];

export function Sidebar({ isOpen, onToggle }: SidebarProps) {
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  // Auto-expand parent items when on a sub-page
  useEffect(() => {
    navigation.forEach((item) => {
      if (item.subItems) {
        const isOnSubPage = item.subItems.some(
          (sub) => pathname === sub.href || pathname.startsWith(sub.href + '/')
        );
        if (isOnSubPage && !expandedItems.includes(item.name)) {
          setExpandedItems((prev) => [...prev, item.name]);
        }
      }
    });
  }, [pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleExpanded = (itemName: string) => {
    setExpandedItems((prev) =>
      prev.includes(itemName) ? prev.filter((name) => name !== itemName) : [...prev, itemName]
    );
  };

  const isItemActive = (item: NavItem) => {
    if (item.subItems) {
      return item.subItems.some((sub) => pathname === sub.href || pathname.startsWith(sub.href + '/'));
    }
    return pathname === item.href;
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-20 lg:hidden" onClick={onToggle}></div>
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-30
          ${isOpen ? 'w-64' : 'lg:w-16 w-64'}
          bg-card border-r border-border
          transform transition-all duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          flex flex-col
        `}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-center lg:justify-start px-3 lg:px-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-flowky-primary to-flowky-secondary flex items-center justify-center text-white font-bold text-lg">
              F
            </div>
            {isOpen && <span className="font-bold text-xl text-foreground">Flowky</span>}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navigation.map((item) => {
            const isActive = isItemActive(item);
            const isExpanded = expandedItems.includes(item.name);
            const hasSubItems = item.subItems && item.subItems.length > 0;

            return (
              <div key={item.name}>
                {/* Main nav item */}
                {hasSubItems ? (
                  <button
                    onClick={() => toggleExpanded(item.name)}
                    className={`
                      w-full flex items-center ${isOpen ? 'gap-3 px-3' : 'justify-center px-2'} py-2 rounded-lg
                      transition-all duration-200
                      ${
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'text-foreground hover:bg-muted'
                      }
                    `}
                    title={!isOpen ? item.name : undefined}
                  >
                    {item.icon}
                    {isOpen && (
                      <>
                        <span className="font-medium flex-1 text-left">{item.name}</span>
                        <svg
                          className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </>
                    )}
                  </button>
                ) : (
                  <Link
                    to={item.href}
                    className={`
                      flex items-center ${isOpen ? 'gap-3 px-3' : 'justify-center px-2'} py-2 rounded-lg
                      transition-all duration-200
                      ${
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'text-foreground hover:bg-muted'
                      }
                    `}
                    title={!isOpen ? item.name : undefined}
                  >
                    {item.icon}
                    {isOpen && <span className="font-medium">{item.name}</span>}
                  </Link>
                )}

                {/* Sub-navigation items */}
                {hasSubItems && isOpen && isExpanded && (
                  <div className="ml-8 mt-1 space-y-1">
                    {item.subItems!.map((subItem) => {
                      const isSubActive = pathname === subItem.href || pathname.startsWith(subItem.href + '/');
                      return (
                        <Link
                          key={subItem.href}
                          to={subItem.href}
                          className={`
                            block px-3 py-1.5 rounded-lg text-sm
                            transition-all duration-200
                            ${
                              isSubActive
                                ? 'bg-primary/10 text-primary font-medium'
                                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                            }
                          `}
                        >
                          {subItem.name}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Bottom Navigation - Settings & Logout */}
        <div className="px-3 py-4 space-y-1 border-t border-border">
          <Link
            to="/settings/profile"
            className={`
              flex items-center ${isOpen ? 'gap-3 px-3' : 'justify-center px-2'} py-2 rounded-lg
              transition-all duration-200
              ${
                pathname === '/settings/profile'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-foreground hover:bg-muted'
              }
            `}
            title={!isOpen ? 'Account' : undefined}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            {isOpen && <span className="font-medium">Account</span>}
          </Link>

          <Link
            to="/settings/integrations"
            className={`
              flex items-center ${isOpen ? 'gap-3 px-3' : 'justify-center px-2'} py-2 rounded-lg
              transition-all duration-200
              ${
                pathname === '/settings/integrations' || pathname.startsWith('/settings/integrations/')
                  ? 'bg-primary text-primary-foreground'
                  : 'text-foreground hover:bg-muted'
              }
            `}
            title={!isOpen ? 'Integrations' : undefined}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            </svg>
            {isOpen && <span className="font-medium">Integrations</span>}
          </Link>

          <button
            onClick={handleLogout}
            className={`
              w-full flex items-center ${isOpen ? 'gap-3 px-3' : 'justify-center px-2'} py-2 rounded-lg
              transition-all duration-200
              text-foreground hover:bg-destructive/10 hover:text-destructive
            `}
            title={!isOpen ? 'Logout' : undefined}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            {isOpen && <span className="font-medium">Logout</span>}
          </button>
        </div>

        {/* Toggle button positioned on right edge */}
        <button
          onClick={onToggle}
          className="hidden lg:flex absolute top-20 -right-4 w-8 h-8 items-center justify-center bg-card border border-border rounded-full hover:bg-muted transition-all duration-300 text-foreground z-40"
          aria-label={isOpen ? 'Hide sidebar' : 'Show sidebar'}
        >
          <svg
            className={`w-4 h-4 transition-transform duration-300 ${!isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
      </aside>
    </>
  );
}
